import React, { useState } from "react";
import {
  Modal,
  ScrollArea,
  Space,
  Stack,
  Text,
  createStyles,
  Group,
  Loader,
  Center,
  Pagination,
  Divider,
} from "@mantine/core";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import useSurveyQuestionnaireResponseApiService from "../services/api/SurveyQuestionnaireResponseApiService";
import SurveyQuestion from "../services/model/SurveyQuestion";

dayjs.extend(relativeTime);

type ViewAllResponsesModalProps = {
  opened: boolean;
  onClose: () => void;
  question: SurveyQuestion;
};

const useStyles = createStyles(() => ({
  responseCard: {
    alignSelf: "stretch",
    flexGrow: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: "5px",
    padding: "14px 16px",
    borderRadius: "4px",
    backgroundColor: "#f7f7f8",
  },
  alternateResponseCard: {
    backgroundColor: "#ffffff",
  },
  modalTitle: {
    fontSize: "18px",
    fontWeight: 700,
    lineHeight: "24.59px",
  },
  questionContent: {
    fontSize: "16px",
    fontWeight: 600,
    marginBottom: "16px",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "16px 0",
  },
  responseCount: {
    fontSize: "14px",
    color: "#868e96",
    textAlign: "center",
    marginBottom: "8px",
  },
}));

const ViewAllResponsesModal: React.FC<ViewAllResponsesModalProps> = ({
  opened,
  onClose,
  question,
}) => {
  const { classes, cx } = useStyles();
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, error } = useSurveyQuestionnaireResponseApiService({
    form_question_id: question.id,
    pagination: true,
    page: currentPage,
  });

  const responses = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = data?.pages || 1;

  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
  };

  const handleModalClose = (): void => {
    setCurrentPage(1); // Reset to first page when closing
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleModalClose}
      title={
        <Text className={classes.modalTitle}>
          All Responses {totalCount > 0 && `(${totalCount})`}
        </Text>
      }
      size="xl"
      centered
      styles={{
        body: {
          maxHeight: "80vh",
        },
      }}
    >
      <Stack style={{ height: "70vh" }} spacing={0}>
        {/* Header Section */}
        <div style={{ flexShrink: 0 }}>
          <Text className={classes.questionContent}>{question.content}</Text>
          {totalCount > 0 && (
            <Text className={classes.responseCount}>
              Showing page {currentPage} of {totalPages} • {totalCount} total
              responses
            </Text>
          )}
        </div>

        {/* Content Section - Flexible */}
        <div style={{ flex: 1, minHeight: 0 }}>
          {isLoading && (
            <Center style={{ height: "100%" }}>
              <Loader />
            </Center>
          )}

          {error && !isLoading && (
            <Center style={{ height: "100%" }}>
              <Text color="red">
                Failed to load responses. Please try again.
              </Text>
            </Center>
          )}

          {!isLoading && !error && responses.length === 0 && (
            <Center style={{ height: "100%" }}>
              <Text color="dimmed">
                No responses available for this question.
              </Text>
            </Center>
          )}

          {!isLoading && !error && responses.length > 0 && (
            <ScrollArea
              style={{
                height: totalPages > 1 ? "400px" : "500px",
              }}
              scrollbarSize={6}
              scrollHideDelay={1500}
            >
              <Stack spacing="md">
                {responses.map((response, index) => (
                  <div
                    key={response.id}
                    className={cx(classes.responseCard, {
                      [classes.alternateResponseCard]: index % 2,
                    })}
                  >
                    <Text size="sm">
                      {response.text_answer ||
                        response.form_question_choice?.content ||
                        "No response"}
                    </Text>
                    <Space h="sm" />
                    <Group spacing="xl">
                      <Text size="sm">
                        {response.employee
                          ? `${response.employee.first_name} ${response.employee.last_name}`
                          : "Unknown Employee"}
                      </Text>
                      <Text size="xs" color="dimmed">
                        {dayjs(response.date_created).fromNow()}
                      </Text>
                    </Group>
                  </div>
                ))}
              </Stack>
            </ScrollArea>
          )}
        </div>

        {/* Footer Section - Pagination */}
        {!isLoading && !error && responses.length > 0 && totalPages > 1 && (
          <div style={{ flexShrink: 0, paddingTop: "16px" }}>
            <Divider my="md" />
            <div className={classes.paginationContainer}>
              <Pagination
                page={currentPage}
                onChange={handlePageChange}
                total={totalPages}
                size="sm"
              />
            </div>
          </div>
        )}
      </Stack>
    </Modal>
  );
};

export default ViewAllResponsesModal;
